import React, { useState } from 'react'

import isEmpty from 'lodash/isEmpty'
import { useHistory } from 'react-router-dom'

import FraudSignalsIframeModal from 'shared/components/Modals/FraudSignalsIframeModal'
import { APPLICATION } from 'shared/config/routes'
import { useGetDocumentDetail } from 'shared/hooks/api'
import useModal from 'shared/hooks/useModal'
import type { Document } from 'shared/interfaces/api/document'
import { getFraudRiskTextAndColor } from 'shared/utils/document'

import FlexContainer from '../FlexContainer'
import { RegularText } from '../Typography'
import Tooltip from '../Tooltip'

import { LumenReadyLink, LumenReadyLinkWithViz } from './styled'
import UnsupportedLabel from './UnsupportedLabel'

export interface LumenReadyProps {
  applicationId: string
  id: string
  isProcessing?: boolean
  canCopyAnalysisId?: boolean
}

const ocrStatus: Record<
  Exclude<Document['ocrJobStatus'], 'succeeded'>,
  string
> = {
  created: 'In Progress',
  failed: 'Failed',
  'in-progress': 'In Progress',
}

const isIframeReady = (row: any): boolean =>
  row?.provider === 'vega' &&
  Boolean(row?.fraudVisualizations?.[0]?.visualization) &&
  Boolean(row?.fraudSubmissionId) &&
  Boolean(row?.url)

export const LumenReady = ({ applicationId, id, isProcessing, canCopyAnalysisId }: LumenReadyProps) => {
  const { document, isLoading, isFetching } = useGetDocumentDetail(id)
  const { openModalDialog } = useModal()
  const history = useHistory()
  const [copied, setCopied] = useState(false)
  const [clipboardUnavailable, setClipboardUnavailable] = useState(false)

  const { ocrJobStatus, fraudRiskLevel } = document || {}

  const isRefetching = isFetching && !isLoading
  const hasValidCachedData = ocrJobStatus === 'succeeded' && fraudRiskLevel

  const allOcrResults: any[] =
    document?.ocrJobsWithOcrResults?.flatMap((job: any) => job.ocrResults ?? []) ?? []

  const iframeReadyRow = allOcrResults.find(isIframeReady)

  const handleFraudClick = (e: React.MouseEvent) => {
    e.preventDefault()

    if (iframeReadyRow) {
      openModalDialog(FraudSignalsIframeModal, {
        documentId: id,
        fraudData: [iframeReadyRow],
      })
      return
    }

    const isVegaDocument = allOcrResults.some((row) => row?.provider === 'vega')
    if (!isVegaDocument) {
      const linkToIAndB = APPLICATION.applicationIncomeAssets.replace(':id', applicationId)
      history.push(linkToIAndB)
    }
    // Vega document with no iframe-ready row: no-op to avoid silent redirect
  }

  if (isProcessing || (isLoading && isEmpty(document)) || (isRefetching && !hasValidCachedData)) {
    return <RegularText>In Progress</RegularText>
  }

  if (isEmpty(document)) return null

  if (!ocrJobStatus) {
    return <RegularText>Unsupported</RegularText>
  }

  if (fraudRiskLevel) {
    const fraudRiskTextAndColor = getFraudRiskTextAndColor(fraudRiskLevel)
    const fraudSubmissionId =
      iframeReadyRow?.fraudSubmissionId ??
      allOcrResults.find(row => row?.provider === 'vega' && row?.fraudSubmissionId)?.fraudSubmissionId
    const showCopy = canCopyAnalysisId && fraudSubmissionId

    const handleCopy = () => {
      if (!fraudSubmissionId) return
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(fraudSubmissionId).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 5000)
        }).catch(() => setClipboardUnavailable(true))
      } else {
        setClipboardUnavailable(true)
      }
    }

    const copyControl = showCopy && (
      clipboardUnavailable ? (
        <input readOnly value={fraudSubmissionId} size={24} style={{ fontSize: 12 }} onClick={(e) => (e.target as HTMLInputElement).select()} />
      ) : (
        <Tooltip title={copied ? 'Copied!' : 'Copy Analysis ID'} theme="dark">
          <button type="button" onClick={handleCopy} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}>
            Copy ID
          </button>
        </Tooltip>
      )
    )

    if (fraudRiskTextAndColor.color === 'unset') {
      return showCopy ? (
        <FlexContainer alignItems="center" gap="8px" flexWrap="wrap">
          <UnsupportedLabel />
          {copyControl}
        </FlexContainer>
      ) : (
        <UnsupportedLabel />
      )
    }

    if (iframeReadyRow) {
      const badge = (
        <Tooltip title="Click to view Risk Indicator details" theme="dark">
          <LumenReadyLinkWithViz href="#" color={fraudRiskTextAndColor.color} onClick={handleFraudClick}>
            {fraudRiskTextAndColor.text}
          </LumenReadyLinkWithViz>
        </Tooltip>
      )
      return showCopy ? <FlexContainer alignItems="center" gap="8px" flexWrap="wrap">{badge}{copyControl}</FlexContainer> : badge
    }

    const link = <LumenReadyLink href="#" color={fraudRiskTextAndColor.color} onClick={handleFraudClick}>{fraudRiskTextAndColor.text}</LumenReadyLink>
    return showCopy ? <FlexContainer alignItems="center" gap="8px" flexWrap="wrap">{link}{copyControl}</FlexContainer> : link
  }

  if (ocrJobStatus in ocrStatus) {
    return <RegularText>{ocrStatus[ocrJobStatus]}</RegularText>
  }

  return <RegularText>In Progress</RegularText>
}
